/**
 * Batch-scan UX rules per device class + browser capabilities.
 * APIs differ by OS/engine; we feature-detect and apply conservative defaults
 * (e.g. no PiP on iOS where video PiP is unreliable for this flow).
 *
 * Static capabilities are computed once in the browser (never cached from SSR).
 */

export type BatchScanDeviceKind = "phone" | "tablet" | "desktop";

/** Align with ExpiryDeskApp: isMobile = width < 768, tablet = 768–1023. */
export function getBatchScanDeviceKind(width: number): BatchScanDeviceKind {
  if (width < 768) return "phone";
  if (width < 1024) return "tablet";
  return "desktop";
}

function detectIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
  return false;
}

function detectAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

function canUseWakeLock(): boolean {
  if (typeof window === "undefined" || !window.isSecureContext) return false;
  return typeof navigator !== "undefined" && typeof navigator.wakeLock?.request === "function";
}

function canUseVideoPictureInPicture(): boolean {
  if (typeof document === "undefined" || !document.pictureInPictureEnabled) return false;
  return (
    typeof HTMLVideoElement !== "undefined" &&
    typeof HTMLVideoElement.prototype.requestPictureInPicture === "function"
  );
}

type StaticCaps = Pick<
  BatchScanUxProfile,
  "isIOS" | "isAndroid" | "flushOnPageHide" | "allowWakeLock" | "allowPictureInPicture"
>;

/** Filled only in the browser after first real read — never from SSR placeholders. */
let browserCaps: StaticCaps | null = null;

function readBrowserCapsOnce(): StaticCaps {
  if (browserCaps) return browserCaps;
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      isIOS: false,
      isAndroid: false,
      flushOnPageHide: false,
      allowWakeLock: false,
      allowPictureInPicture: false,
    };
  }
  const isIOS = detectIOS();
  const isAndroid = detectAndroid();
  const allowWakeLock = canUseWakeLock();
  const pipNative = canUseVideoPictureInPicture();
  const allowPictureInPicture = pipNative && !isIOS;
  browserCaps = {
    isIOS,
    isAndroid,
    flushOnPageHide: isIOS,
    allowWakeLock,
    allowPictureInPicture,
  };
  return browserCaps;
}

export type BatchScanUxProfile = {
  kind: BatchScanDeviceKind;
  isIOS: boolean;
  isAndroid: boolean;
  flushOnPageHide: boolean;
  allowWakeLock: boolean;
  allowPictureInPicture: boolean;
  recoverySaveDebounceMs: number;
};

const DEBOUNCE_MS = { phone: 280, tablet: 340, desktop: 400 } as const;

/**
 * Viewport-dependent fields update on resize; OS / API flags are stable per session.
 */
export function getBatchScanUxProfile(viewportWidth: number): BatchScanUxProfile {
  const kind = getBatchScanDeviceKind(viewportWidth);
  const caps = readBrowserCapsOnce();
  const recoverySaveDebounceMs = DEBOUNCE_MS[kind];
  return { kind, recoverySaveDebounceMs, ...caps };
}
